import { Vector3, Matrix4, Euler } from "three";
import { config, getPhrasesInPercent } from "./config";
import Lines from "./Lines.js";
import Rings from "./Rings.js";
import Mask from "./Mask.js";
import MilestoneManager from "./MilestoneManager.js";
import CameraShaker from "./CameraShaker.js";
import PostProcessor from "./PostProcessor.js";
import { Event } from "~/lib/EventMixin.js";

import { isTouchDevice, map, clamp } from "~/lib/utils.js";

export const PHRASE = {
    ENTERING: "entering",
    STARTING: "starting",
    CHARGING: "charging",
    BOOSTING: "boosting",
    ENDING: "ending",
};

class About extends Event {
    async init(renderer, scene, camera, milestones) {
        // Initialize the milestone blocks in 3D environment using MilestoneManager
        this.milestoneManager = new MilestoneManager();
        this.mileStoneBlocks = this.milestoneManager.init(milestones);

        this.isTouchDevice = isTouchDevice;
        this.isInited = false;
        this.renderer = renderer;
        this.scene = scene;
        this.camera = camera;

        this.pScrollY = -1;
        this.direction = 0;
        this.progress = 0;
        this.pPhrase = null;
        this.phrase = PHRASE.ENTERING;

        this.mask = new Mask();
        this.lines = new Lines();
        this.rings = new Rings();
        this.cameraShaker = new CameraShaker(this.camera);

        this.cachedCameraPosition = new Vector3();
        this.cachedCameraLookAt = new Vector3();
        this.cachedCameraRotation = new Vector3();
        this._lookMatrix = new Matrix4();
        this._rotation = new Euler();

        this.scene.add(this.lines);
        this.scene.add(this.rings);
        this.scene.add(this.mileStoneBlocks);

        this.fovMovement = 0;

        this.postProcessor = new PostProcessor(
            this.renderer,
            this.scene,
            this.camera,
            this.mask,
        );
        await this.postProcessor.init();

        this._onWindowResize();
        this._bindEvents();

        this.isInited = true;
    }

    update(progress, configs) {
        if (!this.isInited) return;

        this.progress = progress;

        this._updateCameraCache(configs);
        this._updatePhrase();
        this._updateFarthestPointScreen();
        this._updateFov();

        this._centerCamera();
        this.cameraShaker.shake(this.phrase);

        this.lines.update(progress);
        this.mask.update(progress);
        this.rings.update(progress);

        this.postProcessor.update();
    }

    reposition() {
        this.mask.reposition();
    }

    destroy() {
        this._unbindEvents();
        this.lines.destroy();
        this.rings.destroy();
        this.mask.destroy();
        this.cameraShaker.destroy();
        this.postProcessor.destroy();

        this.scene.remove(this.lines);
        this.scene.remove(this.rings);
    }

    _bindEvents() {
        if (this.isTouchDevice) {
            window.addEventListener("touchstart", this._onTouchStart);
            window.addEventListener("touchmove", this._onTouchMove);
        }
        window.addEventListener("resize", this._onWindowResize);
        window.addEventListener("scroll", this._onWindowScroll);
    }

    _unbindEvents() {
        if (this.isTouchDevice) {
            window.removeEventListener("touchstart", this._onTouchStart);
            window.removeEventListener("touchmove", this._onTouchMove);
        }
        window.removeEventListener("resize", this._onWindowResize);
        window.removeEventListener("scroll", this._onWindowScroll);
    }

    _onTouchStart = (e) => {
        this._lastTouchY = e.touches[0].clientY;
    };

    _onTouchMove = (e) => {
        const y = e.touches[0].clientY;
        this.direction = this._lastTouchY - y > 0 ? 1 : -1;
        this._lastTouchY = y;
    };

    _onWindowResize = () => {
        // fix for phantom resize in iOS
        if (!this._prevWidth) {
            this._prevWidth = window.innerWidth;
        } else {
            if (this._prevWidth == window.innerWidth) return;
        }

        this.milestoneManager.updateLayout(window.innerWidth);
        this.postProcessor.setSize(window.innerWidth, window.innerHeight);
    };

    _onWindowScroll = () => {
        const scrollY = Math.max(0, window.scrollY);

        // Ignore tiny phantom scrolls
        const diff = Math.abs(scrollY - this.pScrollY);
        if (diff < 5) return;

        if (!this.isTouchDevice) {
            let direction = this.direction;

            if (scrollY > this.pScrollY) {
                direction = 1;
            } else if (scrollY < this.pScrollY) {
                direction = -1;
            }

            this.direction = direction;
        }

        this.pScrollY = scrollY;
        this.lines.direction.value = this.direction;

        const speeds = {
            [PHRASE.ENTERING]: undefined,
            [PHRASE.CHARGING]: undefined,
            [PHRASE.BOOSTING]: 50,
        };

        const speed = speeds[this.phrase];

        if (this.phrase == PHRASE.ENTERING) {
            // mask <-> starting
            if (
                this.direction == 1 &&
                this.progress < this.mask.progressLength
            ) {
                return this._goAhead(speed);
            } else if (
                this.direction == -1 &&
                this.progress > 0 &&
                this.progress <= this.mask.progressLength
            ) {
                return this._goBack(speed);
            }
        }

        if (this.isChargingOrBoosting) {
            // charging <-> boosting <-> ending
            if (this.direction == 1) {
                return this._goAhead(speed);
            } else if (this.direction == -1) {
                return this._goBack(speed);
            }
        }
    };

    _goAhead(amount = 30) {
        if (this.direction != 1 || this.progress >= 1) return;
        scrollBy(0, amount);
    }

    _goBack(amount = 0) {
        // Disable for now
        if (this.direction != -1 || this.progress <= 0) return;
        // scrollBy(0, -amount);
    }

    _updateCameraCache(configs) {
        const { cameraPosition, cameraLookAt } = configs.value;
        if (
            this.cachedCameraPosition.equals(cameraPosition) &&
            this.cachedCameraLookAt.equals(cameraLookAt)
        )
            return;

        this.cachedCameraPosition.copy(cameraPosition);
        this.cachedCameraLookAt.copy(cameraLookAt);

        const up = new Vector3(0, 1, 0);
        this._lookMatrix.lookAt(
            this.cachedCameraPosition,
            this.cachedCameraLookAt,
            up,
        );

        this._rotation.setFromRotationMatrix(this._lookMatrix);
        this.cachedCameraRotation.set(
            this._rotation.x,
            this._rotation.y,
            this._rotation.z,
        );
    }

    _updatePhrase() {
        this.phrase = this._getCurrentPhrase();

        if (this.pPhrase !== this.phrase) {
            this.emit("phraseChanged", this.pPhrase, this.phrase);
            this.pPhrase = this.phrase;
        }
    }

    _updateFov() {
        const chargingStartedProgress = getPhrasesInPercent().starting;

        this.fovMovement = map(this.progress, chargingStartedProgress, 1, 0, 1);
        this.fovMovement = clamp(this.fovMovement, 0, 1);

        this.camera.fov = Math.min(
            130,
            config.camera.fov + this.fovMovement * 100,
        );
        this.camera.updateProjectionMatrix();
    }

    _centerCamera() {
        const centerFactor = 1 - this.fovMovement;

        this.camera.position.x *= centerFactor;
        this.camera.position.y *= centerFactor;

        this.camera.rotation.x = this.cachedCameraRotation.x * centerFactor;
        this.camera.rotation.y = this.cachedCameraRotation.y * centerFactor;
    }

    _getCurrentPhrase() {
        const phrases = getPhrasesInPercent();

        if (this.progress >= phrases.boosting) return PHRASE.ENDING;
        if (this.progress >= phrases.charging) return PHRASE.BOOSTING;
        if (this.progress >= phrases.starting) return PHRASE.CHARGING;
        if (this.progress >= phrases.entering) return PHRASE.STARTING;
        return PHRASE.ENTERING;
    }

    _updateFarthestPointScreen() {
        if (!this.farthestPoint) {
            this.farthestPoint = new Vector3();
        }
        this.farthestPoint.set(0, this.camera.position.y, -config.camera.far);
        this.farthestPointScreen = this.farthestPoint.project(this.camera);
        this.mask.translate(
            this.farthestPointScreen.x,
            this.farthestPointScreen.y,
        );
        this.postProcessor.translate(
            this.farthestPointScreen.x,
            this.farthestPointScreen.y,
        );
    }

    _getMilestoneBlocks() {}

    get progressString() {
        return this.progress.toFixed(2);
    }

    get isChargingOrBoosting() {
        return (
            this.phrase === PHRASE.CHARGING || this.phrase === PHRASE.BOOSTING
        );
    }
}

export const about = new About();

export default about;
